if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      abortController = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartSnapshot = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];
      cartUpdateUnsubscriber = undefined;

      constructor() {
        super();
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get productId() {
        return this.getAttribute('data-product-id');
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get productForm() {
        return this.querySelector('form[is="product-form"]');
      }

      get quantityInput() {
        return this.querySelector('quantity-input input');
      }

      get sharedInventoryEnabled() {
        return this.dataset.sharedInventoryEnabled === 'true';
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = FoxTheme.pubsub.subscribe(
          FoxTheme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.cartUpdateUnsubscriber = FoxTheme.pubsub.subscribe(
          FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate,
          ({ cart }) => {
            this.cartSnapshot = cart;
            this.updateBundleAddButtonFromCart(cart);
            this.refreshSharedInventoryState(cart);
          }
        );

        this.initQuantityHandlers();
        this.productVariants = this.getProductVariants();

        this.currentVariant = this.getSelectedVariant(this);
        if (this.currentVariant) {
          this.forceHiddenPriceOptionVariant();
          this.updateMedia(this.currentVariant);
          this.refreshSharedInventoryState();
          // Initial state for add-to-cart button based on current cart
          (async () => {
            try {
              const cart = await (
                await fetch(`${FoxTheme.routes.cart_url}.js`, { ...FoxTheme.utils.fetchConfig('json', 'GET') })
              ).json();
              this.cartSnapshot = cart;
              this.updateBundleAddButtonFromCart(cart);
              this.refreshSharedInventoryState(cart);
            } catch (e) {
              console.log(e);
            }
          })();
        }
      }

      forceHiddenPriceOptionVariant() {
        const hiddenPriceSelect = this.querySelector('variant-selects select[data-price-option-hidden="true"]');
        if (!hiddenPriceSelect || !this.currentVariant || !Array.isArray(this.currentVariant.options)) return;

        const forcedOption = hiddenPriceSelect.selectedOptions && hiddenPriceSelect.selectedOptions[0];
        if (!forcedOption) return;

        const forcedValue = forcedOption.value;
        if (this.currentVariant.options.includes(forcedValue)) return;

        hiddenPriceSelect.dispatchEvent(new Event('change', { bubbles: true }));
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.disableButtons();
        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;
        const viewMode = this.dataset.viewMode || 'main-product';

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage, viewMode)
            : this.handleUpdateProductInfo(productUrl, viewMode),
        });
      }

      handleSwapProduct(productUrl, updateFullPage, viewMode) {
        return (html) => {
          const quickView = html.querySelector('#MainProduct-quick-view__content');
          if (quickView && viewMode === 'quick-view') {
            html = quickView.content.cloneNode(true);
          }
          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));

          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;
            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
            HTMLUpdateUtility.viewTransition(
              document.getElementById('shopify-section-sticky-atc-bar'),
              html.getElementById('shopify-section-sticky-atc-bar'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );

            if (!variant) {
              this.setUnavailable();
              return;
            }
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }

          this.currentVariant = variant;
          if (this.cartSnapshot) {
            this.updateBundleAddButtonFromCart(this.cartSnapshot);
          }
          this.refreshSharedInventoryState(this.cartSnapshot);
        };
      }

      handleUpdateProductInfo(productUrl, viewMode) {
        return (html) => {
          const quickView = html.querySelector('#MainProduct-quick-view__content');
          if (quickView && viewMode === 'quick-view') {
            html = quickView.content.cloneNode(true);
          }
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateShareUrl(variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(variant);
          this.currentVariant = variant;
          if (this.cartSnapshot) {
            this.updateBundleAddButtonFromCart(this.cartSnapshot);
          }
          this.refreshSharedInventoryState(this.cartSnapshot);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku');
          updateSourceFromDestination('Inventory');
          updateSourceFromDestination('Badges');
          updateSourceFromDestination('PricePerItem');
          updateSourceFromDestination('Volume');

          this.updateQuantityRules(this.sectionId, this.productId, html);
          updateSourceFromDestination('QuantityRules');
          updateSourceFromDestination('VolumeNote');

          HTMLUpdateUtility.viewTransition(
            document.querySelector(`#SizeChart-${this.sectionId}`),
            html.querySelector(`#SizeChart-${this.sectionId}`),
            this.preProcessHtmlCallbacks,
            this.postProcessHtmlCallbacks
          );

          const stickyAtcBar = document.getElementById(`shopify-section-sticky-atc-bar`);
          if (stickyAtcBar) {
            stickyAtcBar.classList.remove('hidden');
          }

          const newAddButton = html.getElementById(`ProductSubmitButton-${this.sectionId}`);
          const isDisabled = !newAddButton || newAddButton.hasAttribute('disabled');
          this.updateButtonsState(isDisabled, {
            updateText: true,
            text: isDisabled ? FoxTheme.variantStrings.soldOut : null,
          });

          FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });

          document.dispatchEvent(
            new CustomEvent('variant:changed', {
              detail: {
                variant: variant,
              },
            })
          );
        };
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      getProductVariants() {
        const variantsJson = this.querySelector('[data-product-variants]')?.textContent;
        if (!variantsJson) return [];

        try {
          const variants = JSON.parse(variantsJson);
          return Array.isArray(variants) ? variants : [];
        } catch (error) {
          console.error(error);
          return [];
        }
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      updateURL(url, variantId) {
        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      updateVariantInputs(variantId) {
        document
          .querySelectorAll(`#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`)
          .forEach((productForm) => {
            const input = productForm.querySelector('input[name="id"]');
            input.value = variantId ?? '';
            input.dispatchEvent(new Event('change', { bubbles: true }));
          });
      }

      updateMedia(variant) {
        const productMedia = this.querySelector(`[id^="MediaGallery-${this.dataset.section}"]`);
        if (!productMedia) return; // Early return if productMedia is not found

        const setActiveMedia = () => {
          if (typeof productMedia.setActiveMedia === 'function') {
            productMedia.init();
            productMedia.setActiveMedia(variant);
            return true; // Indicate success
          }
          return false; // Indicate failure
        };

        if (!setActiveMedia()) {
          this.timer = setInterval(() => {
            if (setActiveMedia()) {
              clearInterval(this.timer);
            }
          }, 100);
        }
      }

      updateShareUrl(variantId) {
        if (!variantId) return;
        const shareButton = document.getElementById(`ProductShare-${this.dataset.section}`);
        if (!shareButton || !shareButton.updateUrl) return;
        shareButton.updateUrl(`${window.shopUrl}${this.dataset.url}?variant=${variantId}`);
      }

      updateButtonsState(disabled, options = {}) {
        const { updateText = false, text = null, updateStyles = true } = options;

        // Handle main add button in product form
        const productForm = document.getElementById(`product-form-${this.dataset.section}`);
        if (productForm) {
          const addButton = productForm.querySelector('[name="add"]');
          const addButtonText = productForm.querySelector('[name="add"] > span');

          if (addButton) {
            if (disabled) {
              addButton.setAttribute('disabled', 'disabled');
              if (updateText && text && addButtonText) {
                addButtonText.textContent = this.decoded(text);
              }
            } else {
              const variantId = productForm.querySelector('[name="id"]')?.value;
              // Respect bundle-disabled flag: don't re-enable if we've disabled because bundle is in cart.
              if (variantId && addButton.dataset.bundleDisabled !== 'true') {
                addButton.removeAttribute('disabled');
                if (updateText && addButtonText) {
                  addButtonText.textContent = this.decoded(FoxTheme.variantStrings.addToCart);
                }
              }
            }

            if (updateStyles) {
              addButton.style.pointerEvents = disabled ? 'none' : '';
              addButton.style.opacity = disabled ? '0.6' : '';
            }
          }
        }

        // Handle sticky ATC button
        const otherButtonSelectors = ['.sticky-atc-bar [name="add"]'];
        otherButtonSelectors.forEach((selector) => {
          const buttons = document.querySelectorAll(selector);
          buttons.forEach((button) => {
            const buttonText = button.querySelector('span');
            if (disabled) {
              button.setAttribute('disabled', 'disabled');
              if (updateText && text && buttonText) {
                buttonText.textContent = this.decoded(text);
              }
              if (updateStyles) {
                button.style.pointerEvents = 'none';
                button.style.opacity = '0.6';
              }
            } else {
              if (button.dataset.bundleDisabled !== 'true') {
                button.removeAttribute('disabled');
                if (updateText && buttonText) {
                  buttonText.textContent = this.decoded(FoxTheme.variantStrings.addToCart);
                }
                if (updateStyles) {
                  button.style.pointerEvents = '';
                  button.style.opacity = '';
                }
              }
            }
          });
        });
      }

      getSharedInventoryVariants(variantId = this.currentVariant?.id) {
        if (!this.sharedInventoryEnabled || !variantId) return [];
        if (!Array.isArray(this.productVariants) || this.productVariants.length !== 2) return [];

        const sharedVariants = [...this.productVariants];
        const isCurrentVariantInGroup = sharedVariants.some((variant) => String(variant.id) === String(variantId));

        if (!isCurrentVariantInGroup || sharedVariants.length !== 2) return [];

        const isManagedByShopify = sharedVariants.every(
          (variant) => variant.inventory_management === 'shopify' && variant.inventory_policy !== 'continue'
        );

        return isManagedByShopify ? sharedVariants : [];
      }

      getFormQuantity(form) {
        const quantityField = form?.querySelector('input[name="quantity"]');
        const fallbackMin = parseInt(quantityField?.dataset.min || quantityField?.getAttribute('min') || '1', 10);
        const parsedQuantity = parseInt(quantityField?.value || `${fallbackMin}`, 10);

        if (Number.isNaN(parsedQuantity) || parsedQuantity < 1) return fallbackMin || 1;

        return parsedQuantity;
      }

      getCartQuantityForVariantIds(variantIds, cart = this.cartSnapshot) {
        if (!Array.isArray(cart?.items) || !Array.isArray(variantIds) || !variantIds.length) return 0;

        return cart.items.reduce((total, item) => {
          return variantIds.includes(String(item.variant_id)) ? total + item.quantity : total;
        }, 0);
      }

      getYarnSharedInventoryState(cart = this.cartSnapshot) {
        const activeWrapper = this.querySelector(`.yarn-rows[data-variant-id="${this.currentVariant?.id}"]:not(.hidden)`);
        if (!activeWrapper) {
          return {
            enabled: false,
            valid: true,
            soldOut: false,
            message: null,
          };
        }

        const rows = Array.from(activeWrapper.querySelectorAll('.yarn-row'));
        const groups = new Map();

        rows.forEach((row) => {
          const dropdown = row.querySelector('[data-yarn-dropdown]');
          const qtyInput = row.querySelector('.qty-input');
          if (!dropdown || !qtyInput) return;
          if (dropdown.dataset.sharedInventoryEnabled !== 'true') return;

          const variantIds = (dropdown.dataset.sharedInventoryVariantIds || '')
            .split(',')
            .map((value) => value.trim())
            .filter(Boolean);
          const baseInventory = parseInt(dropdown.dataset.sharedInventoryBase || '', 10);

          if (!variantIds.length || Number.isNaN(baseInventory)) return;

          const requestedQty = parseFloat(qtyInput.value || qtyInput.getAttribute('min') || '0');
          const key = variantIds.join(',');
          const existing = groups.get(key) || {
            variantIds,
            baseInventory,
            requestedQty: 0,
            label: row.querySelector('.yarn-row__label')?.textContent?.trim() || 'Yarn',
          };

          existing.requestedQty += Number.isNaN(requestedQty) ? 0 : requestedQty;
          groups.set(key, existing);
        });

        if (!groups.size) {
          return {
            enabled: false,
            valid: true,
            soldOut: false,
            message: null,
          };
        }

        for (const group of groups.values()) {
          const cartQty = this.getCartQuantityForVariantIds(group.variantIds, cart);
          const remaining = Math.max(0, group.baseInventory - cartQty);

          if (remaining <= 0) {
            return {
              enabled: true,
              valid: false,
              soldOut: true,
              message: `${group.label} is sold out because it shares inventory with its linked variant.`,
            };
          }

          if (group.requestedQty > remaining) {
            return {
              enabled: true,
              valid: false,
              soldOut: false,
              message: `You can only add ${remaining} of ${group.label} across its linked variants.`,
            };
          }
        }

        return {
          enabled: true,
          valid: true,
          soldOut: false,
          message: null,
        };
      }

      getRecipeSharedInventoryState(cart = this.cartSnapshot) {
        const recipeInput = this.querySelector('[data-recipe-variant-id]');
        if (!recipeInput || recipeInput.dataset.recipeSharedInventoryEnabled !== 'true') {
          return {
            enabled: false,
            valid: true,
            soldOut: false,
            message: null,
          };
        }

        const variantIds = (recipeInput.dataset.recipeSharedInventoryVariantIds || '')
          .split(',')
          .map((value) => value.trim())
          .filter(Boolean);
        const baseInventory = parseInt(recipeInput.dataset.recipeSharedInventoryBase || '', 10);
        const recipeTitle = recipeInput.dataset.recipeTitle || 'Recipe';

        if (!variantIds.length || Number.isNaN(baseInventory)) {
          return {
            enabled: false,
            valid: true,
            soldOut: false,
            message: null,
          };
        }

        const cartQty = this.getCartQuantityForVariantIds(variantIds, cart);
        const remaining = Math.max(0, baseInventory - cartQty);
        const requestedQty = recipeInput.value ? 1 : 0;

        if (remaining <= 0) {
          return {
            enabled: true,
            valid: false,
            soldOut: true,
            message: `${recipeTitle} is sold out because it shares inventory with its linked variant.`,
          };
        }

        if (requestedQty > remaining) {
          return {
            enabled: true,
            valid: false,
            soldOut: false,
            message: `You can only add ${remaining} of ${recipeTitle} across its linked variants.`,
          };
        }

        return {
          enabled: true,
          valid: true,
          soldOut: false,
          message: null,
        };
      }

      getSharedInventoryState({ variantId = this.currentVariant?.id, quantity = null, cart = this.cartSnapshot } = {}) {
        const sharedVariants = this.getSharedInventoryVariants(variantId);
        if (!sharedVariants.length) {
          return {
            enabled: false,
            soldOut: false,
            remaining: null,
            requestedQuantity: quantity,
            availableForRequest: true,
          };
        }

        const sharedInventory = Math.min(
          ...sharedVariants.map((variant) => {
            const inventoryQuantity = parseInt(variant.inventory_quantity, 10);
            return Number.isNaN(inventoryQuantity) ? 0 : inventoryQuantity;
          })
        );

        const cartQuantity = Array.isArray(cart?.items)
          ? cart.items.reduce((total, item) => {
              return sharedVariants.some((variant) => String(variant.id) === String(item.variant_id))
                ? total + item.quantity
                : total;
            }, 0)
          : 0;

        const remaining = Math.max(0, sharedInventory - cartQuantity);
        const requestedQuantity = quantity ?? this.getFormQuantity(this.productForm);
        const soldOut = remaining <= 0;

        return {
          enabled: true,
          soldOut,
          remaining,
          requestedQuantity,
          availableForRequest: !soldOut && requestedQuantity <= remaining,
        };
      }

      syncSharedInventoryQuantity(state) {
        if (!this.quantityInput || !state.enabled) return;

        if (this.quantityInput.dataset.originalMax === undefined) {
          this.quantityInput.dataset.originalMax = this.quantityInput.getAttribute('max') || '';
        }

        const minQuantity = parseInt(
          this.quantityInput.dataset.min || this.quantityInput.getAttribute('min') || '1',
          10
        );
        const originalMax = this.quantityInput.dataset.originalMax;
        const maxQuantity = originalMax ? Math.min(parseInt(originalMax, 10), state.remaining) : state.remaining;

        if (state.remaining > 0) {
          this.quantityInput.setAttribute('max', `${Math.max(minQuantity, maxQuantity)}`);
          if (this.getFormQuantity(this.productForm) > maxQuantity) {
            this.quantityInput.value = `${Math.max(minQuantity, maxQuantity)}`;
          }
        } else {
          this.quantityInput.setAttribute('max', `${minQuantity}`);
          this.quantityInput.value = `${minQuantity}`;
        }
      }

      refreshSharedInventoryState(cart = this.cartSnapshot) {
        const state = this.getSharedInventoryState({ cart });
        this.sharedInventoryState = state;
        const yarnState = this.getYarnSharedInventoryState(cart);
        this.yarnSharedInventoryState = yarnState;
        const recipeState = this.getRecipeSharedInventoryState(cart);
        this.recipeSharedInventoryState = recipeState;

        if (state.enabled) {
          this.syncSharedInventoryQuantity(state);

          if (state.soldOut) {
            this.updateButtonsState(true, {
              updateText: true,
              text: FoxTheme.variantStrings.soldOut,
            });
            return;
          }

          if (!state.availableForRequest) {
            this.updateButtonsState(true, {
              updateText: true,
              text: FoxTheme.variantStrings.addToCart,
            });
            return;
          }
        }

        if (yarnState.enabled && !yarnState.valid) {
          this.updateButtonsState(true, {
            updateText: true,
            text: yarnState.soldOut ? FoxTheme.variantStrings.soldOut : FoxTheme.variantStrings.addToCart,
          });
          return;
        }

        if (recipeState.enabled && !recipeState.valid) {
          this.updateButtonsState(true, {
            updateText: true,
            text: recipeState.soldOut ? FoxTheme.variantStrings.soldOut : FoxTheme.variantStrings.addToCart,
          });
          return;
        }

        if (this.currentVariant?.available) {
          this.updateButtonsState(false, {
            updateText: true,
            text: FoxTheme.variantStrings.addToCart,
          });
        }
      }

      validateSharedInventoryBeforeSubmit(form = this.productForm) {
        if (!form) return true;

        const variantId = form.querySelector('[name="id"]')?.value;
        const quantity = this.getFormQuantity(form);
        const state = this.getSharedInventoryState({ variantId, quantity, cart: this.cartSnapshot });
        if (state.enabled) {
          if (state.soldOut) {
            form.displayFormErrors?.('Denne variant er udsolgt, fordi den deler lager med den anden variant.');
            return false;
          }

          if (!state.availableForRequest) {
            form.displayFormErrors?.(
              `Du kan kun tilfoje ${state.remaining} stk. samlet pa tvaers af begge varianter.`
            );
            return false;
          }
        }

        const yarnState = this.getYarnSharedInventoryState(this.cartSnapshot);
        if (yarnState.enabled && !yarnState.valid) {
          form.displayFormErrors?.(yarnState.message);
          return false;
        }

        const recipeState = this.getRecipeSharedInventoryState(this.cartSnapshot);
        if (recipeState.enabled && !recipeState.valid) {
          form.displayFormErrors?.(recipeState.message);
          return false;
        }

        form.displayFormErrors?.(false);
        return true;
      }

      // Disable/enable add-to-cart when this bundle already exists in cart
      setBundleAddDisabled(disabled) {
        const productForm = document.getElementById(`product-form-${this.dataset.section}`);
        if (!productForm) return;
        const addButton = productForm.querySelector('[name="add"]');
        if (!addButton) return;

        if (disabled) {
          addButton.dataset.bundleDisabled = 'true';
          addButton.setAttribute('disabled', 'disabled');
        } else {
          if (addButton.dataset.bundleDisabled === 'true') {
            delete addButton.dataset.bundleDisabled;
            addButton.removeAttribute('disabled');
          }
        }
      }

      updateBundleAddButtonFromCart(cart) {
        if (!cart || !Array.isArray(cart.items)) return;
        const variantId = this.currentVariant?.id;
        if (!variantId) return;

        const hasMainBundle = cart.items.some((item) => {
          const props = item.properties || {};
          return String(item.variant_id) === String(variantId) && !props._bundle_parent;
        });

        this.setBundleAddDisabled(hasMainBundle);
      }

      decoded(text) {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = text;
        const decoded = tempDiv.textContent;
        return decoded;
      }

      setUnavailable() {
        this.updateButtonsState(true, {
          updateText: true,
          text: FoxTheme.variantStrings.unavailable,
        });
        const price = document.getElementById(`price-${this.dataset.section}`);
        const inventory = document.getElementById(`Inventory-${this.dataset.section}`);
        const sku = document.getElementById(`Sku-${this.dataset.section}`);
        const volumePricing = document.getElementById(`Volume-${this.dataset.section}`);
        const stickyAtcBar = document.getElementById(`shopify-section-sticky-atc-bar`);

        if (price) price.classList.add('hidden');
        if (inventory) inventory.classList.add('hidden');
        if (sku) sku.classList.add('hidden');
        if (volumePricing) volumePricing.classList.add('hidden');
        if (stickyAtcBar) stickyAtcBar.classList.add('hidden');
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.setQuantityBoundries();
        this.quantityInput.addEventListener('input', () => this.refreshSharedInventoryState(this.cartSnapshot));
        this.quantityInput.addEventListener('change', () => this.refreshSharedInventoryState(this.cartSnapshot));
        if (!this.hasAttribute('data-original-section')) {
          this.cartUpdateUnsubscriber = FoxTheme.pubsub.subscribe(
            FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate,
            this.fetchQuantityRules.bind(this)
          );
        }
      }

      setQuantityBoundries() {
        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.quantityBoundries, {
          data: {
            sectionId: this.sectionId,
            productId: this.productId,
          },
        });
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.productIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart')?.classList.add('btn--loading');

        fetch(`${this.getAttribute('data-url')}?variant=${currentVariantId}&section_id=${this.sectionId}`)
          .then((response) => response.text())
          .then((responseText) => {
            const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.sectionId, this.productId, parsedHTML);
          })
          .catch((error) => {
            console.error(error);
          })
          .finally(() => {
            this.querySelector('.quantity__rules-cart')?.classList.remove('btn--loading');
          });
      }

      updateQuantityRules(sectionId, productId, parsedHTML) {
        if (!this.quantityInput) return;

        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.quantityRules, {
          data: {
            sectionId,
            productId,
            parsedHTML,
          },
        });

        this.setQuantityBoundries();
      }

      disableButtons() {
        this.updateButtonsState(true);
      }

      enableButtons() {
        this.updateButtonsState(false);
      }
    }
  );
}
